import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { Search, Send, Smile, ArrowLeft, Circle, MessageCircle, RefreshCw, Wifi, WifiOff } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { api, WS_BASE } from '../../api';

const relativeTime = (ts) => {
  if (!ts) return '';
  const diff = (Date.now() - new Date(ts).getTime()) / 1000;
  if (diff < 60) return 'now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return new Date(ts).toLocaleDateString();
};

const QUICK_EMOJI = ['😀', '😂', '🔥', '👏', '💯', '😡', '🏆', '❤️'];

function Avatar({ src, name, size = 40, showOnline }) {
  const [failed, setFailed] = useState(false);
  const initials = (name || '?')[0]?.toUpperCase();
  return (
    <div className="chat-avatar" style={{ width: size, height: size, minWidth: size }}>
      {src && !failed ? (
        <img src={src} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
          onError={() => setFailed(true)} />
      ) : (
        <span style={{ fontSize: size * 0.38 }}>{initials}</span>
      )}
      {showOnline && <span className="avatar-online-dot" />}
    </div>
  );
}

export default function ChatPage() {
  const { user, token } = useAuth();
  const [users, setUsers] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [input, setInput] = useState('');
  const [showEmoji, setShowEmoji] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const ws = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const loadingTimer = useRef(null);

  // Decode the JWT to get the authoritative Django user_id — the profile endpoint
  // may return a profile-level PK that differs from auth.User.id stored in the token.
  const myId = React.useMemo(() => {
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const jwtId = payload.user_id ?? payload.sub ?? null;
        if (jwtId !== null && jwtId !== undefined) return jwtId;
      } catch { }
    }
    return user?.id ?? user?.user_id ?? user?.profile_id;
  }, [token, user]);

  const getPhoto = (u) => {
    if (!u) return null;
    const raw = u.profile_picture || u.photo || u.avatar || u.picture;
    if (!raw) return null;
    if (raw.startsWith('http')) return raw;
    return `https://backproject.meanc.net${raw}`;
  };
  const getName = (u) => u?.username || u?.user_name || u?.first_name || `User ${u?.id || '?'}`;

  const loadUsers = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const [follows, directory, convs] = await Promise.allSettled([
        api.getFollows(token),
        api.getChatDirectory(token),
        api.getConversations(token),
      ]);
      const dir = follows.status === 'fulfilled' ? (Array.isArray(follows.value) ? follows.value : follows.value?.results ?? []) : [];
      const chatDir = directory.status === 'fulfilled' ? (Array.isArray(directory.value) ? directory.value : directory.value?.results ?? []) : [];
      const convList = conversations.status === 'fulfilled' ? (Array.isArray(conversations.value) ? conversations.value : conversations.value?.results ?? []) : [];

      // Build user list from follows + chat directory
      const seen = new Set();
      const merged = [];
      [...chatDir, ...dir].forEach(entry => {
        const u = entry?.follower_info || entry?.followed_info || entry?.follower_profile || entry?.followed_profile || entry;
        const id = u?.id ?? u?.user_id ?? u?.profile_id;
        if (!id || seen.has(String(id))) return;
        if (id === myId || String(id) === String(myId)) return;
        seen.add(String(id));
        merged.push({ ...u, id });
      });
      setUsers(merged);
      if (convs.status === 'fulfilled') {
        setConversations(Array.isArray(convs.value) ? convs.value : convs.value?.results ?? []);
      }
    } catch {
      setError('Could not load contacts.');
    } finally {
      setLoading(false);
    }
  }, [token, myId]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const openChat = useCallback((user) => {
    if (ws.current) ws.current.close();
    if (loadingTimer.current) clearTimeout(loadingTimer.current);
    setSelectedUser(user);
    setMessages([]);
    setInput('');
    setWsConnected(false);
    setLoadingMsgs(true);

    const wsUrl = `${WS_BASE}/${user.id}/?token=${token}`;
    const socket = new WebSocket(wsUrl);
    ws.current = socket;

    socket.onopen = () => {
      setWsConnected(true);
      // Phase 1: send recipient_id so the server sets up (or retrieves) the conversation
      // and immediately replies with all_messages.
      socket.send(JSON.stringify({ type: 'fetch_messages', recipient_id: user.id }));
      // Safety timeout — if all_messages never arrives (e.g. server-side error),
      // stop the loading spinner so the user isn't stuck forever.
      loadingTimer.current = setTimeout(() => setLoadingMsgs(false), 6000);
    };

    socket.onmessage = (event) => {
      let data;
      try { data = JSON.parse(event.data); } catch { return; }

      if (data.type === 'all_messages') {
        if (loadingTimer.current) { clearTimeout(loadingTimer.current); loadingTimer.current = null; }
        setMessages((data.messages || []).map(m => ({ ...m, confirmed: true })));
        setLoadingMsgs(false);
      } else if (data.type === 'chat_message') {
        if (String(data.sender_id) === String(myId)) {
          // Echo of our own sent message — confirm the pending bubble by tempId
          setMessages(prev => prev.map(m =>
            m.tempId === data.tempId ? { ...m, confirmed: true } : m
          ));
        } else {
          // Incoming message from the other person
          setMessages(prev => [...prev, {
            message: data.message,
            sender_id: data.sender_id,
            confirmed: true,
            timestamp: data.timestamp || new Date().toISOString(),
          }]);
        }
      } else if (data.type === 'error') {
        if (loadingTimer.current) { clearTimeout(loadingTimer.current); loadingTimer.current = null; }
        setLoadingMsgs(false);
      }
    };

    socket.onclose = () => {
      setWsConnected(false);
      if (loadingTimer.current) { clearTimeout(loadingTimer.current); loadingTimer.current = null; }
      setLoadingMsgs(false);
    };
    socket.onerror = () => {
      setWsConnected(false);
      if (loadingTimer.current) { clearTimeout(loadingTimer.current); loadingTimer.current = null; }
      setLoadingMsgs(false);
    };
  }, [token, myId]);

  const closeChat = () => {
    if (ws.current) ws.current.close();
    if (loadingTimer.current) { clearTimeout(loadingTimer.current); loadingTimer.current = null; }
    setSelectedUser(null);
    setMessages([]);
    setLoadingMsgs(false);
  };

  const sendMessage = () => {
    if (!input.trim() || !ws.current || ws.current.readyState !== WebSocket.OPEN) return;
    const tempId = Date.now();
    const text = input.trim();
    setMessages(prev => [...prev, { sender_id: myId, message: text, confirmed: false, timestamp: new Date().toISOString(), tempId }]);
    ws.current.send(JSON.stringify({ type: 'chat_message', message: text, recipient_id: selectedUser.id, tempId }));
    setInput('');
    setShowEmoji(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const filteredUsers = users.filter(u => {
    const n = getName(u).toLowerCase();
    return !search || n.includes(search.toLowerCase());
  });

  // Find last message per user from conversations
  const getLastMsg = (userId) => {
    const conv = conversations.find(c => {
      const participants = c.participants || c.members || [];
      return participants.some(p => String(p?.id ?? p?.user_id ?? p) === String(userId));
    });
    return conv?.last_message?.content || conv?.last_message?.message || '';
  };

  return (
    <div className="chat-page">
      {/* Users sidebar */}
      <div className={`chat-sidebar ${selectedUser ? 'chat-sidebar-hidden-mobile' : ''}`}>
        <div className="chat-sidebar-header">
          <h2 className="chat-sidebar-title">
            <MessageCircle size={20} />
            Messages
          </h2>
          <button className="icon-btn" onClick={loadUsers} title="Refresh">
            <RefreshCw size={16} />
          </button>
        </div>

        <div className="chat-search-wrap">
          <Search size={14} color="#64748B" />
          <input className="chat-search" placeholder="Search contacts…" value={search}
            onChange={e => setSearch(e.target.value)} />
        </div>

        <div className="chat-user-list">
          {loading ? (
            [1, 2, 3].map(i => (
              <div key={i} className="chat-user-skeleton">
                <div className="sk-circle" />
                <div className="sk-lines"><div className="sk-line wide" /><div className="sk-line narrow" /></div>
              </div>
            ))
          ) : error ? (
            <div className="chat-empty">
              <p>{error}</p>
              <button onClick={loadUsers} style={{ color: '#38BDF8', cursor: 'pointer' }}>Retry</button>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="chat-empty">
              <MessageCircle size={32} color="rgba(14,165,233,0.4)" />
              <p>No contacts yet</p>
              <span>Follow people to start chatting</span>
            </div>
          ) : filteredUsers.map(u => (
            <div key={u.id} className={`chat-user-item ${selectedUser?.id === u.id ? 'active' : ''}`}
              onClick={() => openChat(u)}>
              <Avatar src={getPhoto(u)} name={getName(u)} />
              <div className="chat-user-info">
                <div className="chat-user-name">{getName(u)}</div>
                <div className="chat-user-last">{getLastMsg(u.id) || 'Start a conversation'}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Chat window */}
      <div className={`chat-window ${selectedUser ? 'chat-window-visible' : ''}`}>
        {selectedUser ? (
          <>
            {/* Chat header */}
            <div className="chat-window-header">
              <button className="icon-btn chat-back-btn" onClick={closeChat}>
                <ArrowLeft size={20} />
              </button>
              <Avatar src={getPhoto(selectedUser)} name={getName(selectedUser)} showOnline={wsConnected} />
              <div className="chat-window-user-info">
                <div className="chat-window-name">{getName(selectedUser)}</div>
                <div className={`chat-window-status ${wsConnected ? 'connected' : ''}`}>
                  {wsConnected ? (
                    <><Wifi size={12} /><span>Online</span></>
                  ) : (
                    <><WifiOff size={12} /><span>Connecting…</span></>
                  )}
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="chat-messages">
              {loadingMsgs ? (
                <div className="chat-loading">
                  <div className="auth-spinner" />
                  <span>Loading messages…</span>
                </div>
              ) : messages.length === 0 ? (
                <div className="chat-messages-empty">
                  <MessageCircle size={40} color="rgba(14,165,233,0.4)" />
                  <p>No messages yet</p>
                  <span>Say hello! 👋</span>
                </div>
              ) : (
                messages.map((msg, i) => {
                  const isMe = String(msg.sender_id) === String(myId);
                  return (
                    <div key={msg.tempId || `${msg.timestamp}-${i}`}
                      className={`chat-bubble-wrap ${isMe ? 'mine' : 'theirs'}`}>
                      {!isMe && (
                        <Avatar src={getPhoto(selectedUser)} name={getName(selectedUser)} size={28} />
                      )}
                      <div className={`chat-bubble ${isMe ? 'bubble-mine' : 'bubble-theirs'} ${msg.confirmed ? '' : 'bubble-pending'}`}>
                        <span>{msg.message}</span>
                        <span className="bubble-time">{relativeTime(msg.timestamp)}</span>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Emoji picker */}
            {showEmoji && (
              <div className="chat-emoji-bar">
                {QUICK_EMOJI.map(e => (
                  <button key={e} className="emoji-btn" onClick={() => setInput(s => s + e)}>{e}</button>
                ))}
              </div>
            )}

            {/* Input */}
            <div className="chat-input-wrap">
              <button className="chat-emoji-toggle" onClick={() => setShowEmoji(s => !s)}>
                <Smile size={20} color="#64748B" />
              </button>
              <textarea
                ref={inputRef}
                className="chat-input"
                placeholder="Type a message…"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={1}
              />
              <button className="chat-send-btn" onClick={sendMessage}
                disabled={!input.trim() || !wsConnected}>
                <Send size={18} />
              </button>
            </div>
          </>
        ) : (
          <div className="chat-no-selection">
            <MessageCircle size={56} color="rgba(14,165,233,0.3)" />
            <h3>Your Messages</h3>
            <p>Select a contact to start chatting</p>
          </div>
        )}
      </div>
    </div>
  );
}
